import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up"
            >
                <div className="overflow-hidden bg-muted aspect-video">
                    {article.thumbnail && (
                        <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                    )}
                </div>
                <div className="pt-5">
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-accent">{label}</span>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] mt-3 group-hover:text-accent transition-colors duration-150">
                        {article.title}
                    </h2>
                    {article.excerpt && <p className="mt-4 text-base text-muted-foreground max-w-2xl leading-relaxed">{article.excerpt}</p>}
                    <div className="mt-4 flex items-center gap-3 font-mono text-xs text-muted-foreground">
                        {article.author && <span>{article.author}</span>}
                        <span>{timeAgo(article.published_at)}</span>
                    </div>
                </div>
            </Link>
        );
    }

    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-4 py-4 border-b border-hair"
            >
                <div className="flex-1 min-w-0">
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">{label}</span>
                    <h3 className="font-serif text-base font-bold leading-snug mt-1 group-hover:text-accent transition-colors duration-150">
                        {article.title}
                    </h3>
                    <span className="font-mono text-xs text-muted-foreground mt-1 block">{timeAgo(article.published_at)}</span>
                </div>
                {article.thumbnail && (
                    <div className="w-24 h-20 shrink-0 overflow-hidden bg-muted">
                        <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                )}
            </Link>
        );
    }

    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group block animate-fade-up"
        >
            <div className="overflow-hidden bg-muted aspect-[3/2]">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                ) : (
                    <div className="w-full h-full bg-muted" />
                )}
            </div>
            <div className="pt-4">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">{label}</span>
                <h3 className="font-serif text-xl font-bold leading-snug mt-2 group-hover:text-accent transition-colors duration-150">
                    {article.title}
                </h3>
                {article.excerpt && <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{article.excerpt}</p>}
                <div className="mt-3 flex items-center gap-3 font-mono text-xs text-muted-foreground">
                    {article.author && <span className="truncate max-w-[160px]">{article.author}</span>}
                    <span>{timeAgo(article.published_at)}</span>
                </div>
            </div>
        </Link>
    );
}

export { timeAgo };
